import { Stack, Typography } from '@mui/material';
import React from 'react';
import { generatePath } from 'react-router';

import { ContactDto, ContactInfoTypeDto, ResourceTypeDto } from '@/api/generated';
import {
  RowDetailsContactLink,
  RowDetailsError,
  RowDetailsItem,
  RowDetailsLayout,
  RowDetailsLink,
  RowDetailsLoading,
  RowDetailsMore,
  RowDetailsMuted,
  RowDetailsOpenButton,
  RowDetailsSection,
  RowDetailsText,
} from '@/components/table/expandable/RowDetails.comp.tsx';
import { OpenTasksSection } from '@/components/table/expandable/RowDetailsSections.comp.tsx';
import { SmartDate } from '@/components/ui/smartDate/SmartDate.comp.tsx';
import { UserAvatar } from '@/components/ui/userAvatar/UserAvatar.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useGetProcessDetails } from '@/views/processesDetails/util/useGetProcessDetails.util.ts';

type Props = {
  detailsPath: string;
  processId: string;
};

const PROCESS_TASK_RESOURCE_TYPES = [ResourceTypeDto.Process, ResourceTypeDto.ProcessOffer];

const SHOWN_CONTACTS = 3;

const ProcessContact: React.FC<{ contact: ContactDto }> = ({ contact }) => {
  const { basicContact, contactInfos } = contact;
  const name =
    `${basicContact.firstName} ${basicContact.lastName}`.trim() || basicContact.displayName;
  const phones = contactInfos.filter(info => info.contactInfoType === ContactInfoTypeDto.Phone);
  const emails = contactInfos.filter(info => info.contactInfoType === ContactInfoTypeDto.Email);

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <RowDetailsLink
        to={generatePath(RouteEnum.CONTACTS_DETAILS, {
          contactId: basicContact.contactId.toString(),
        })}
      >
        {name}
      </RowDetailsLink>
      {basicContact.companyPosition && (
        <Typography noWrap sx={{ color: themeColors.grey }} variant="f12Regular">
          {basicContact.companyPosition}
        </Typography>
      )}
      {phones.slice(0, 1).map(info => (
        <RowDetailsContactLink key={info.contactInfoId} kind="phone" value={info.value} />
      ))}
      {emails.slice(0, 1).map(info => (
        <RowDetailsContactLink key={info.contactInfoId} kind="email" value={info.value} />
      ))}
    </Stack>
  );
};

export const ProcessesListRowDetails: React.FC<Props> = ({ detailsPath, processId }) => {
  const { t } = useTranslationWithPrefix('views.processes.table.details');
  const { t: tBody } = useTranslationWithPrefix('views.processes.table.body');
  const { t: tSections } = useTranslationWithPrefix('components.listDetails.sections');
  const { isErrorGettingProcessDetails, isPendingProcessDetails, processDetails } =
    useGetProcessDetails({ processId });
  const contacts = processDetails?.customerContacts ?? [];

  return (
    <RowDetailsLayout actions={<RowDetailsOpenButton label={t('open')} to={detailsPath} />}>
      <OpenTasksSection
        moreTo={detailsPath}
        resourceId={processId}
        resourceTypes={PROCESS_TASK_RESOURCE_TYPES}
      />
      <RowDetailsSection title={t('customer')}>
        {isPendingProcessDetails && <RowDetailsLoading />}
        {isErrorGettingProcessDetails && <RowDetailsError />}
        {processDetails && (
          <>
            {processDetails.customer ? (
              <RowDetailsLink
                to={generatePath(RouteEnum.CUSTOMERS_DETAILS, {
                  customerId: processDetails.customer.customerId.toString(),
                })}
              >
                {processDetails.customer.name}
              </RowDetailsLink>
            ) : (
              <RowDetailsMuted text={t('noCustomer')} />
            )}
            <RowDetailsItem label={t('contacts')}>
              <Stack spacing={1}>
                {contacts.length === 0 && <RowDetailsMuted text={t('noContacts')} />}
                {contacts.slice(0, SHOWN_CONTACTS).map(contact => (
                  <ProcessContact contact={contact} key={contact.basicContact.contactId} />
                ))}
                <RowDetailsMore count={contacts.length - SHOWN_CONTACTS} to={detailsPath} />
              </Stack>
            </RowDetailsItem>
          </>
        )}
      </RowDetailsSection>
      <RowDetailsSection title={tSections('details')}>
        {isPendingProcessDetails && <RowDetailsLoading />}
        {processDetails && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={2}>
              <RowDetailsItem label={tBody('owner')}>
                <UserAvatar person={processDetails.processOwner} size={22} withName />
              </RowDetailsItem>
              <RowDetailsItem label={tBody('creator')}>
                <UserAvatar person={processDetails.processCreator} size={22} withName />
              </RowDetailsItem>
            </Stack>
            <Stack direction="row" spacing={2}>
              <RowDetailsItem label={tBody('createdAt')}>
                <SmartDate date={processDetails.createdAt} />
              </RowDetailsItem>
              <RowDetailsItem label={t('updatedAt')}>
                <SmartDate date={processDetails.updatedAt} />
              </RowDetailsItem>
            </Stack>
            {processDetails.eventType && (
              <RowDetailsItem label={t('eventType')}>
                {processDetails.eventType.name}
              </RowDetailsItem>
            )}
            {processDetails.description?.trim() && (
              <RowDetailsItem label={t('description')}>
                <RowDetailsText lines={3} text={processDetails.description} />
              </RowDetailsItem>
            )}
          </Stack>
        )}
      </RowDetailsSection>
    </RowDetailsLayout>
  );
};
